import * as React from "react";
import { Link } from "react-router";

import { useAuth, useNavigation } from "@/providers";
import { PageLayout, RobotoLinkHoverUnderline } from "@/shared/components";
import { CreateEntityButton } from "@/shared/components/CreateEntityButton";
import { TriggerNameLink } from "@/shared/components/links";
import {
  QuickSearch,
  QuickSearchButton,
  qualifyPrincipal,
  renderPrincipal,
  ResourceView,
  useQuickSearchHotkey,
  useResourceView,
  type QuickSearchProperty,
  type Filter,
  type FilterMatchMode,
} from "@/shared/components/ResourceView";
import { useAgentDirectory } from "@/shared/domain/agents/hooks";
import {
  eventLabel,
  isSchedule,
  readableCron,
  TARGET_TYPE_LABELS,
  TriggerTargetType,
  type PaginationState,
  type TriggerTargetSpec,
  type TriggerRecord,
} from "@/shared/domain/triggers";
import { useTriggers } from "@/shared/domain/triggers/hooks";
import { ENTITY_ICONS } from "@/shared/foundation/icons";
import { Banner } from "@/shared/foundation/primitives";
import {
  cellStyles,
  colId,
  ColumnVisibility,
  createColumnHelper,
  DataTable,
  FLEX_FILL_COL_DEF,
  renderDashOrValue,
  type ColDef,
  type ColumnPickerOption,
  type DataTableLoadingState,
  type ICellRendererParams,
} from "@/shared/foundation/primitives/Table";
import { Goto } from "@/shared/navigation";

import { TriggerOwnerBadge } from "../Trigger/TriggerOwnerBadge";

import styles from "./ListTriggers.module.css";
import { TriggerEnabledSwitch } from "./TriggerEnabledSwitch";
import { TRIGGER_FILTER_DEFINITIONS } from "./triggerFilterDefinitions";

const NO_FILTERS: readonly Filter[] = [];

// Quick find routes a `tr_`-shaped value to the id and anything else to the
// name, as the Datasets list does.
const QUICK_SEARCH_PROPERTIES: QuickSearchProperty[] = [
  { field: "trigger_id", name: "ID", pattern: /^tr_\S+$/ },
  { field: "name", name: "Name", fallback: true },
];

/**
 * Columns shown on a first visit; the picker offers the rest, including the
 * ID, which matters when matching one trigger rather than scanning the list.
 */
const DEFAULT_VISIBLE_COLUMN_IDS = [
  "name",
  "fires_on",
  "targets",
  "modified",
  "modified_by",
  "enabled",
];
const NO_TRIGGERS: TriggerRecord[] = [];

function targetSummary(
  target: TriggerTargetSpec,
  agentNames: ReadonlyMap<string, string>,
): string {
  switch (target.type) {
    case TriggerTargetType.InvokeAction:
      return `${TARGET_TYPE_LABELS[target.type]}: ${target.action.name}`;
    case TriggerTargetType.StartAgent:
      return `${TARGET_TYPE_LABELS[target.type]}: ${
        agentNames.get(target.agent_id) ?? target.agent_id
      }`;
    case TriggerTargetType.SendSlackMessage:
      return TARGET_TYPE_LABELS[target.type];
  }
}

function firesOnSummary(trigger: TriggerRecord): string {
  return isSchedule(trigger.fires_on)
    ? readableCron(trigger.fires_on.cron)
    : trigger.fires_on.events.map(eventLabel).join(", ");
}

const columnHelper = createColumnHelper<TriggerRecord>();

/**
 * Why the table is empty. Nothing before the first load succeeds, since
 * whether there are any triggers is then unknown, or when only this page is
 * empty because a refresh removed its triggers.
 */
function NoTriggers({
  paginationState,
  hasActiveFilters,
  onCreate,
}: {
  paginationState: PaginationState | undefined;
  hasActiveFilters: boolean;
  onCreate: () => void;
}) {
  if (paginationState === undefined || paginationState.total > 0) {
    return null;
  }
  return (
    <ResourceView.EmptyState
      hasActiveFilters={hasActiveFilters}
      noData={{
        icon: ENTITY_ICONS.trigger,
        title: "No triggers yet",
        description: (
          <>
            Run an action or start an agent when an event happens or on a
            schedule. To control how files are ingested, use{" "}
            <Link to={Goto.route.Settings.Ingestion.Root}>Ingestion rules</Link>
            .
          </>
        ),
        action: (
          <CreateEntityButton
            entity="trigger"
            variant="secondary"
            onClick={onCreate}
          />
        ),
      }}
      noMatches={{
        icon: ENTITY_ICONS.trigger,
        title: "No matching triggers",
        description: "Try adjusting or clearing your filters.",
      }}
    />
  );
}

/**
 * The `/triggers` page: every trigger in the org, event-fired and
 * schedule-fired alike, as one filterable table. There is one form to create a trigger, so there is one
 * view to see them; "scheduled" is a filter, not a tab.
 *
 * Triggers are deleted from their own page, where it's clear what is being
 * deleted.
 */
export function ListTriggers() {
  const { goto } = useNavigation();

  // A failed toggle is reported in the page's alert, not by its row.
  const [toggleError, setToggleError] = React.useState<Error>();

  const { currentOrganization } = useAuth();
  const agentDirectory = useAgentDirectory(currentOrganization?.org_id);
  const agentNames = React.useMemo(
    () =>
      new Map(
        agentDirectory.agents.map((agent) => [agent.agent_id, agent.name]),
      ),
    [agentDirectory.agents],
  );

  const view = useResourceView<TriggerRecord>({
    getRowId: (row) => row.trigger_id,
    initialSort: { columnId: "modified", direction: "desc" },
    defaultVisibleColumnIds: DEFAULT_VISIBLE_COLUMN_IDS,
  });
  const quickSearch = useQuickSearchHotkey();
  const structuredFilters =
    view.filters.type === "structured" ? view.filters.filters : NO_FILTERS;
  const matchMode: FilterMatchMode =
    view.filters.type === "structured" ? view.filters.matchMode : "AND";

  const { sort, currentPage, pageSize } = view.table;
  const filter = { filters: structuredFilters, matchMode };
  const triggers = useTriggers({
    sort,
    filter,
    page: currentPage,
    pageSize,
  });
  const { paginationState } = triggers;
  const error = toggleError ?? triggers.error ?? undefined;

  const clearToggleError = React.useCallback(
    () => setToggleError(undefined),
    [],
  );

  // The primitive hides columns unless a visibility picker opts them in; this
  // list has no picker and shows every column, so flip the default.
  const columns = React.useMemo<ColDef<TriggerRecord>[]>(() => {
    const baseColumns: ColDef<TriggerRecord>[] = [
      {
        colId: "trigger_id",
        headerName: "ID",
        minWidth: 150,
        sortable: false,
        valueGetter: (p) => p.data?.trigger_id ?? "",
        cellRenderer: (p: ICellRendererParams<TriggerRecord>) =>
          p.data ? (
            <RobotoLinkHoverUnderline
              to={Goto.createTriggerRoute(p.data.name)}
              className={cellStyles.idCell}
            >
              {p.data.trigger_id}
            </RobotoLinkHoverUnderline>
          ) : null,
      },
      {
        colId: "name",
        headerName: "Name",
        flex: 2,
        sortable: true,
        valueGetter: (p) => p.data?.name ?? "",
        minWidth: 200,
        cellRenderer: (p: ICellRendererParams<TriggerRecord>) =>
          p.data ? (
            <span className={styles.nameCell}>
              <TriggerNameLink
                triggerName={p.data.name}
                className={cellStyles.primaryContent}
              />
              <TriggerOwnerBadge trigger={p.data} />
            </span>
          ) : null,
      },
      columnHelper.accessor(firesOnSummary, {
        colId: "fires_on",
        headerName: "Fires on",
        flex: 2,
        minWidth: 120,
        sortable: false,
        cellRenderer: renderDashOrValue,
      }),
      columnHelper.accessor(
        (trigger) =>
          trigger.targets
            .map((target) => targetSummary(target, agentNames))
            .join(", "),
        {
          colId: "targets",
          headerName: "Targets",
          flex: 2,
          minWidth: 120,
          sortable: false,
          cellRenderer: renderDashOrValue,
        },
      ),
      columnHelper.timestamp("modified", {
        headerName: "Modified",
        sortable: true,
        variant: "relative",
      }),
      {
        colId: "modified_by",
        headerName: "Modified by",
        minWidth: 150,
        valueGetter: (p) => qualifyPrincipal(p.data?.modified_by),
        cellRenderer: renderPrincipal,
      },
      {
        colId: "enabled",
        headerName: "Enabled",
        flex: 0,
        width: 110,
        cellRenderer: (p: ICellRendererParams<TriggerRecord>) =>
          p.data ? (
            <TriggerEnabledSwitch
              trigger={p.data}
              onToggle={clearToggleError}
              onError={setToggleError}
            />
          ) : null,
      },
    ];
    return baseColumns.map((column) => ({ hide: false, ...column }));
  }, [agentNames, clearToggleError]);
  const pickerOptions = React.useMemo<ColumnPickerOption[]>(
    () =>
      columns.map((column) => ({
        id: colId(column),
        label: column.headerName ?? colId(column),
      })),
    [columns],
  );

  // Skeletons only while there's no list to show. Unlike deriveLoadingState, a
  // background refetch keeps the rows on screen.
  const loading: DataTableLoadingState = triggers.isPending
    ? "rowsOnly"
    : "none";

  return (
    <PageLayout title="Roboto - Triggers" headerContentLeft="Triggers">
      <div className={styles.page}>
        {error !== undefined && (
          <Banner variant="error" className={styles.errorAlert}>
            {error.message}
          </Banner>
        )}
        <QuickSearch
          open={quickSearch.isOpen}
          onClose={quickSearch.close}
          properties={QUICK_SEARCH_PROPERTIES}
          placeholder="Search by ID or name…"
          onSubmit={(quickFilter) => {
            view.clearFilters();
            view.commit({ type: "structured", filter: quickFilter });
          }}
        />
        <ResourceView.Root>
          <ResourceView.Actions>
            <QuickSearchButton
              onClick={quickSearch.open}
              label="Find triggers"
            />
            <ResourceView.Filters
              filterDefinitions={TRIGGER_FILTER_DEFINITIONS}
              filters={view.filters}
              onFilterSelected={view.commit}
            />
            <ColumnVisibility
              options={pickerOptions}
              visibleColumnIds={view.table.visibleColumnIds}
              setColumnVisibility={view.table.setColumnVisibility}
            />
            <div className={styles.actionsRight}>
              <CreateEntityButton
                entity="trigger"
                onClick={() => goto.createTrigger()}
              />
            </div>
          </ResourceView.Actions>
          <ResourceView.ActiveFilters
            filters={view.filters}
            filterDefinitions={TRIGGER_FILTER_DEFINITIONS}
            onRemove={view.removeFilter}
            onUpdate={view.updateFilter}
            onClear={view.clearFilters}
            onMatchModeChange={view.setMatchMode}
            onFilterSelected={view.commit}
          />
          <DataTable<TriggerRecord>
            table={view.table}
            ariaLabel="Triggers"
            hasNextPage={paginationState?.hasNextPage ?? false}
            loading={loading}
            columns={columns}
            defaultColDef={FLEX_FILL_COL_DEF}
            data={paginationState?.rows ?? NO_TRIGGERS}
            totalRowCount={paginationState?.total}
            noRowsContent={
              <NoTriggers
                paginationState={paginationState}
                hasActiveFilters={view.hasActiveFilters}
                onCreate={() => goto.createTrigger()}
              />
            }
          />
        </ResourceView.Root>
      </div>
    </PageLayout>
  );
}
